import { useState, useEffect, useMemo } from 'react'
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts'
// Shared signed-in client + paged reader: the email tables' RLS policy is
// authenticated-only, and both tables are well past PostgREST's 1000-row cap
// (10.1k entries / 30.9k events on 2026-09-07).
import { fetchAllRows } from '../../lib/supabase'

const DARK_TOOLTIP = {
  backgroundColor: '#1f2937',
  border: '1px solid #374151',
  borderRadius: '8px',
  color: '#fff'
}

export default function DashboardTab() {
  const [stats, setStats] = useState({
    totalSent: 0,
    delivered: 0,
    opened: 0,
    clicked: 0,
    uniqueClicked: 0,
    bounced: 0,
    failed: 0,
    pending: 0
  })
  const [recentActivity, setRecentActivity] = useState([])
  const [allEntries, setAllEntries] = useState([])
  const [allEvents, setAllEvents] = useState([])
  const [loading, setLoading] = useState(true)

  // Analytics accordion state
  const [analyticsOpen, setAnalyticsOpen] = useState(false)
  const [engagementOpen, setEngagementOpen] = useState(false)
  const [statusDistOpen, setStatusDistOpen] = useState(false)
  const [hourlyOpen, setHourlyOpen] = useState(false)

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      setLoading(true)

      // Get every email sent (paged past the 1000-row cap)
      const entries = await fetchAllRows(
        'EmailEntries',
        'EmailEntriesId, ToEmail, Created, ExternalId',
        { column: 'Created', ascending: false }
      )

      setAllEntries(entries)
      const totalSent = entries.length

      // Get every delivery event, newest first
      const events = await fetchAllRows(
        'EmailEntryDeliveryEvents',
        'Id, Status, Timestamp, EmailEntryId',
        { column: 'Timestamp', ascending: false }
      )

      setAllEvents(events)

      // Count events by type
      const eventCounts = {
        delivered: 0,
        opened: 0,
        clicked: 0,
        uniqueClicked: 0,
        bounced: 0,
        failed: 0
      }

      // Track unique clicks per email
      const emailsWithClicks = new Set()

      if (events) {
        events.forEach(event => {
          const status = String(event.Status || '').toLowerCase()

          // Status vocab spans two writers: SendGrid raw names (delivered/open/
          // click/bounce) via Email-Webhook, and Redirect-Url's 'clicked'. Count
          // both forms so neither producer is missed (read == writer).
          if (status === 'delivered') eventCounts.delivered++
          else if (status === 'open' || status === 'opened') eventCounts.opened++
          else if (status === 'click' || status === 'clicked') {
            eventCounts.clicked++
            // Count unique emails that received clicks
            emailsWithClicks.add(event.EmailEntryId)
          }
          else if (status === 'bounce' || status === 'bounced' || status === 'dropped') eventCounts.bounced++
          else if (status === 'deferred' || status === 'spamreport') eventCounts.failed++
        })
      }

      eventCounts.uniqueClicked = emailsWithClicks.size

      // Calculate pending (sent but no delivery confirmation)
      const deliveredIds = new Set()
      if (events) {
        events.forEach(event => {
          const status = String(event.Status || '').toLowerCase()
          if (['delivered', 'bounce', 'bounced', 'dropped'].includes(status)) {
            deliveredIds.add(event.EmailEntryId)
          }
        })
      }
      const pending = totalSent - deliveredIds.size

      setStats({
        totalSent,
        ...eventCounts,
        pending
      })

      // Get recent activity (last 10 emails with their latest event)
      const recent = entries?.slice(0, 10).map(entry => {
        const entryEvents = events?.filter(e => e.EmailEntryId === entry.EmailEntriesId)
        const latestEvent = entryEvents?.sort((a, b) =>
          new Date(b.Timestamp) - new Date(a.Timestamp)
        )[0]

        return {
          ...entry,
          latestStatus: latestEvent?.Status || 'sent',
          latestTimestamp: latestEvent?.Timestamp || entry.Created
        }
      }) || []

      setRecentActivity(recent)

    } catch (error) {
      console.error('Failed to load dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  // --- Computed chart data ---

  const dailyData = useMemo(() => {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 29)
    const dayMap = {}
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const key = d.toISOString().slice(0, 10)
      dayMap[key] = { date: key, sent: 0, delivered: 0 }
    }
    allEntries.forEach(e => {
      const day = new Date(e.Created).toISOString().slice(0, 10)
      if (dayMap[day]) dayMap[day].sent++
    })
    allEvents.forEach(e => {
      const status = String(e.Status || '').toLowerCase()
      if (status === 'delivered') {
        const day = new Date(e.Timestamp).toISOString().slice(0, 10)
        if (dayMap[day]) dayMap[day].delivered++
      }
    })
    return Object.values(dayMap)
  }, [allEntries, allEvents])

  const statusDistData = useMemo(() => {
    return [
      { name: 'Delivered', value: stats.delivered, color: '#22c55e' },
      { name: 'Opened', value: stats.opened, color: '#a855f7' },
      { name: 'Clicked', value: stats.clicked, color: '#6366f1' },
      { name: 'Bounced', value: stats.bounced, color: '#ef4444' },
      { name: 'Pending', value: stats.pending, color: '#eab308' },
    ].filter(d => d.value > 0)
  }, [stats])

  const hourlyData = useMemo(() => {
    const hours = Array.from({ length: 24 }, (_, i) => ({ hour: `${i}:00`, events: 0 }))
    allEvents.forEach(e => {
      const h = new Date(e.Timestamp).getHours()
      hours[h].events++
    })
    return hours
  }, [allEvents])

  const engagementRates = useMemo(() => {
    const deliveryRate = stats.totalSent > 0 ? (stats.delivered / stats.totalSent) * 100 : 0
    const openRate = stats.delivered > 0 ? (stats.opened / stats.delivered) * 100 : 0
    const clickRate = stats.opened > 0 ? (stats.uniqueClicked / stats.opened) * 100 : 0
    return { deliveryRate, openRate, clickRate }
  }, [stats])

  const getStatColor = (type) => {
    switch(type) {
      case 'totalSent': return 'bg-blue-500'
      case 'delivered': return 'bg-green-500'
      case 'opened': return 'bg-purple-500'
      case 'clicked': return 'bg-indigo-500'
      case 'bounced': return 'bg-red-500'
      case 'failed': return 'bg-orange-500'
      case 'pending': return 'bg-yellow-500'
      default: return 'bg-gray-500'
    }
  }

  const getStatIcon = (type) => {
    switch(type) {
      case 'totalSent':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        )
      case 'delivered':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      case 'opened':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        )
      case 'clicked':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
          </svg>
        )
      case 'bounced':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      case 'failed':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        )
      case 'pending':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      default:
        return null
    }
  }

  const getPercentage = (value, total) => {
    if (total === 0) return 0
    return Math.round((value / total) * 100)
  }

  const getTooltip = (type) => {
    switch(type) {
      case 'totalSent':
        return 'Total number of emails sent through the system'
      case 'delivered':
        return 'Emails successfully delivered to recipient mail servers. Calculated as (delivered emails / total sent) x 100'
      case 'opened':
        return 'Number of emails opened by recipients. Calculated as (opened emails / delivered emails) x 100'
      case 'clicked':
        return 'Total count of all link clicks across all emails, including multiple clicks from the same email'
      case 'uniqueClicked':
        return 'Number of unique emails that received at least one click. Each email counted once regardless of multiple clicks. Calculated as (emails with clicks / opened emails) x 100'
      case 'bounced':
        return 'Emails that bounced or were dropped by recipient servers. Calculated as (bounced emails / total sent) x 100'
      case 'failed':
        return 'Emails that were deferred or marked as spam'
      case 'pending':
        return 'Emails sent but not yet confirmed as delivered, bounced, or dropped'
      default:
        return ''
    }
  }

  const getStatusBadge = (status) => {
    const statusLower = String(status || 'sent').toLowerCase()

    if (statusLower === 'delivered') {
      return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full">Delivered</span>
    } else if (statusLower === 'open' || statusLower === 'opened') {
      return <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded-full">Opened</span>
    } else if (statusLower === 'click' || statusLower === 'clicked') {
      // 'click' is SendGrid's raw name via Email-Webhook; 'clicked' is written by Redirect-Url.
      return <span className="px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200 rounded-full">Clicked</span>
    } else if (statusLower === 'bounce' || statusLower === 'bounced' || statusLower === 'dropped') {
      return <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full">Bounced</span>
    } else if (statusLower === 'processed') {
      return <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 rounded-full">Processing</span>
    }

    return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200 rounded-full capitalize">{status}</span>
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Email Service Dashboard</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Overview of email delivery performance</p>
        </div>
        <button
          onClick={loadDashboardData}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span>Refresh</span>
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Sent */}
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 cursor-help transition hover:shadow-lg"
          title={getTooltip('totalSent')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Sent</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stats.totalSent}</p>
            </div>
            <div className={`${getStatColor('totalSent')} p-3 rounded-lg text-white`}>
              {getStatIcon('totalSent')}
            </div>
          </div>
        </div>

        {/* Delivered */}
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 cursor-help transition hover:shadow-lg"
          title={getTooltip('delivered')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Delivered</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stats.delivered}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {getPercentage(stats.delivered, stats.totalSent)}% delivery rate
              </p>
            </div>
            <div className={`${getStatColor('delivered')} p-3 rounded-lg text-white`}>
              {getStatIcon('delivered')}
            </div>
          </div>
        </div>

        {/* Opened */}
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 cursor-help transition hover:shadow-lg"
          title={getTooltip('opened')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Opened</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stats.opened}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {getPercentage(stats.opened, stats.delivered)}% open rate
              </p>
            </div>
            <div className={`${getStatColor('opened')} p-3 rounded-lg text-white`}>
              {getStatIcon('opened')}
            </div>
          </div>
        </div>

        {/* Total Clicks */}
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 cursor-help transition hover:shadow-lg"
          title={getTooltip('clicked')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Clicks</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stats.clicked}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                All clicks across emails
              </p>
            </div>
            <div className={`${getStatColor('clicked')} p-3 rounded-lg text-white`}>
              {getStatIcon('clicked')}
            </div>
          </div>
        </div>

        {/* Unique Clicks */}
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 cursor-help transition hover:shadow-lg"
          title={getTooltip('uniqueClicked')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Unique Clicks</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stats.uniqueClicked}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {getPercentage(stats.uniqueClicked, stats.opened)}% unique click rate
              </p>
            </div>
            <div className={`${getStatColor('clicked')} p-3 rounded-lg text-white`}>
              {getStatIcon('clicked')}
            </div>
          </div>
        </div>

        {/* Bounced */}
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 cursor-help transition hover:shadow-lg"
          title={getTooltip('bounced')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Bounced</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stats.bounced}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {getPercentage(stats.bounced, stats.totalSent)}% bounce rate
              </p>
            </div>
            <div className={`${getStatColor('bounced')} p-3 rounded-lg text-white`}>
              {getStatIcon('bounced')}
            </div>
          </div>
        </div>

        {/* Failed */}
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 cursor-help transition hover:shadow-lg"
          title={getTooltip('failed')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Failed</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stats.failed}</p>
            </div>
            <div className={`${getStatColor('failed')} p-3 rounded-lg text-white`}>
              {getStatIcon('failed')}
            </div>
          </div>
        </div>

        {/* Pending */}
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 cursor-help transition hover:shadow-lg"
          title={getTooltip('pending')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pending Delivery</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stats.pending}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Awaiting delivery confirmation
              </p>
            </div>
            <div className={`${getStatColor('pending')} p-3 rounded-lg text-white`}>
              {getStatIcon('pending')}
            </div>
          </div>
        </div>
      </div>

      {/* Performance Analytics Accordion */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setAnalyticsOpen(!analyticsOpen)}
          className="w-full px-6 py-4 flex items-center justify-between text-left border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
        >
          <div className="flex items-center space-x-3">
            <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Performance Analytics</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">30-day trends and distribution insights</p>
            </div>
          </div>
          <svg
            className={`w-5 h-5 text-gray-500 transition-transform ${analyticsOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {analyticsOpen && (
          <div className="p-6 space-y-4">
            {/* Daily Volume Chart - Always visible when analytics open */}
            <div className="mb-6">
              <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-2">Daily Email Volume</h4>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Track your sending patterns and delivery over the last 30 days</p>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={dailyData}>
                    <defs>
                      <linearGradient id="colorSent" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorDelivered" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: '#9ca3af', fontSize: 12 }}
                      tickLine={{ stroke: '#374151' }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fill: '#9ca3af', fontSize: 12 }}
                      tickLine={{ stroke: '#374151' }}
                    />
                    <Tooltip contentStyle={DARK_TOOLTIP} />
                    <Legend />
                    <Area type="monotone" dataKey="sent" name="Sent" stroke="#3b82f6" fillOpacity={1} fill="url(#colorSent)" />
                    <Area type="monotone" dataKey="delivered" name="Delivered" stroke="#22c55e" fillOpacity={1} fill="url(#colorDelivered)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Engagement Rates Sub-Accordion */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <button
                onClick={() => setEngagementOpen(!engagementOpen)}
                className="w-full px-4 py-3 flex items-center justify-between text-left bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-1.5 bg-green-100 dark:bg-green-900 rounded">
                    <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">Engagement Rates</span>
                </div>
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform ${engagementOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {engagementOpen && (
                <div className="p-4 bg-white dark:bg-gray-800">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Key delivery and engagement metrics</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Delivery Rate */}
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Delivery Rate</p>
                      <p className="text-3xl font-bold text-green-500 mt-1">{engagementRates.deliveryRate.toFixed(1)}%</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{stats.delivered} of {stats.totalSent} sent</p>
                      <div className="mt-3 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-green-500 h-2 rounded-full transition-all"
                          style={{ width: `${Math.min(engagementRates.deliveryRate, 100)}%` }}
                        />
                      </div>
                    </div>
                    {/* Open Rate */}
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Open Rate</p>
                      <p className="text-3xl font-bold text-purple-500 mt-1">{engagementRates.openRate.toFixed(1)}%</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{stats.opened} of {stats.delivered} delivered</p>
                      <div className="mt-3 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-purple-500 h-2 rounded-full transition-all"
                          style={{ width: `${Math.min(engagementRates.openRate, 100)}%` }}
                        />
                      </div>
                    </div>
                    {/* Click Rate */}
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Click Rate</p>
                      <p className="text-3xl font-bold text-indigo-500 mt-1">{engagementRates.clickRate.toFixed(1)}%</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{stats.uniqueClicked} of {stats.opened} opened</p>
                      <div className="mt-3 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-indigo-500 h-2 rounded-full transition-all"
                          style={{ width: `${Math.min(engagementRates.clickRate, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Status Distribution Sub-Accordion */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <button
                onClick={() => setStatusDistOpen(!statusDistOpen)}
                className="w-full px-4 py-3 flex items-center justify-between text-left bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-1.5 bg-indigo-100 dark:bg-indigo-900 rounded">
                    <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                    </svg>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">Status Distribution</span>
                </div>
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform ${statusDistOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {statusDistOpen && (
                <div className="p-4 bg-white dark:bg-gray-800">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Overall breakdown of email delivery outcomes</p>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={statusDistData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          labelLine={{ stroke: '#9ca3af' }}
                        >
                          {statusDistData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={DARK_TOOLTIP} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>

            {/* Hourly Activity Sub-Accordion */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <button
                onClick={() => setHourlyOpen(!hourlyOpen)}
                className="w-full px-4 py-3 flex items-center justify-between text-left bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-1.5 bg-purple-100 dark:bg-purple-900 rounded">
                    <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">Hourly Activity</span>
                </div>
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform ${hourlyOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {hourlyOpen && (
                <div className="p-4 bg-white dark:bg-gray-800">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Email event distribution by hour of day</p>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={hourlyData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                        <XAxis
                          dataKey="hour"
                          tick={{ fill: '#9ca3af', fontSize: 11 }}
                          interval="preserveStartEnd"
                        />
                        <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                        <Tooltip contentStyle={DARK_TOOLTIP} />
                        <Legend />
                        <Bar dataKey="events" name="Events" fill="#a855f7" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Activity</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Last 10 emails sent</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Recipient
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Last Updated
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {recentActivity.length > 0 ? (
                recentActivity.map((activity) => (
                  <tr key={activity.EmailEntriesId} className="hover:bg-gray-50 dark:hover:bg-gray-900 transition">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {activity.ToEmail}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(activity.latestStatus)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(activity.latestTimestamp).toLocaleString()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    No recent activity
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
